<template>
  <div class="w-full max-w-4xl mx-auto px-4">
    <!-- Review Flow Settings -->
    <h3
      class="text-xl font-semibold mb-4 mt-8 bg-gray-900 rounded-lg px-4 py-2"
    >
      Review Flow Settings
    </h3>
    <div class="space-y-4 mb-8">
      <div
        v-for="setting in reviewFlowSettings"
        :key="setting.key"
        class="flex flex-col sm:flex-row sm:items-center gap-2"
      >
        <div class="sm:w-[300px]">
          <label class="font-medium block sm:text-left">
            {{ setting.name }}
          </label>
        </div>
        <div class="flex-1 sm:w-[400px] flex items-center">
          <el-tooltip
            v-if="showTooltip(setting)"
            :content="tooltipContent(setting)"
            placement="right"
          >
            <el-switch
              v-model="userStore.settings[setting.key]"
              :disabled="isDisabled(setting)"
              :data-track="
                JSON.stringify({
                  id: `settings-review-flow-${setting.key}`,
                  type: 'settings-toggle',
                  section: 'review-flow',
                  setting: setting.name,
                  value: userStore.settings[setting.key],
                })
              "
            />
          </el-tooltip>
          <el-switch
            v-else
            v-model="userStore.settings[setting.key]"
            :disabled="isDisabled(setting)"
            :data-track="
              JSON.stringify({
                id: `settings-review-flow-${setting.key}`,
                type: 'settings-toggle',
                section: 'review-flow',
                setting: setting.name,
                value: userStore.settings[setting.key],
              })
            "
          />
        </div>
      </div>
    </div>

    <!-- Payment Settings -->
    <h3 class="text-xl font-semibold mb-4 bg-gray-900 rounded-lg px-4 py-2">
      Payment Settings
    </h3>

    <!-- Payment Methods -->
    <h4 class="text-md font-medium mb-2 ml-2">Payment Methods</h4>
    <div class="space-y-4 mb-8">
      <div
        v-for="setting in paymentMethodSettings"
        :key="setting.key"
        class="flex flex-col sm:flex-row sm:items-center gap-2"
      >
        <div class="sm:w-[300px]">
          <label class="font-medium block sm:text-left">
            {{ setting.name }}
          </label>
        </div>
        <div class="flex-1 sm:w-[400px] flex items-center">
          <el-tooltip
            v-if="showTooltip(setting)"
            :content="tooltipContent(setting)"
            placement="right"
          >
            <el-switch
              v-model="userStore.settings[setting.key]"
              :disabled="isDisabled(setting)"
              :data-track="
                JSON.stringify({
                  id: `settings-payment-method-${setting.key}`,
                  type: 'settings-toggle',
                  section: 'payment-methods',
                  setting: setting.name,
                  value: userStore.settings[setting.key],
                })
              "
            />
          </el-tooltip>
          <el-switch
            v-else
            v-model="userStore.settings[setting.key]"
            :disabled="isDisabled(setting)"
            :data-track="
              JSON.stringify({
                id: `settings-payment-method-${setting.key}`,
                type: 'settings-toggle',
                section: 'payment-methods',
                setting: setting.name,
                value: userStore.settings[setting.key],
              })
            "
          />
        </div>
      </div>
    </div>

    <!-- Payment Details -->
    <h4 class="text-md font-medium mb-2 ml-2">Payment Details</h4>
    <div class="space-y-4 mb-8">
      <div
        v-for="setting in paymentDetailsSettings"
        :key="setting.key"
        class="flex flex-col sm:flex-row sm:items-center gap-2"
      >
        <div class="sm:w-[300px]">
          <label class="font-medium block sm:text-left">
            {{ setting.name }}
          </label>
        </div>
        <div class="flex-1 sm:w-[400px]">
          <el-input
            v-model="userStore.settings[setting.key]"
            :data-track="
              JSON.stringify({
                id: `settings-payment-details-${setting.key}`,
                type: 'settings-input',
                section: 'payment-details',
                setting: setting.name,
              })
            "
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ElSwitch, ElTooltip, ElInput } from "element-plus";
import { useUser } from "@/stores";
import { useResponsiveWidth } from "@/composables/useResponsiveWidth";
import { usePaymentSettings } from "@/composables/usePaymentSettings";
import {
  reviewFlowSettings,
  paymentMethodSettings,
  paymentDetailsSettings,
} from "@/utils/settingsConfig";

const userStore = useUser();
const { inputWidth } = useResponsiveWidth();
const { isDisabled, showTooltip, tooltipContent } = usePaymentSettings();
</script>

<style scoped>
:deep(.el-table__header) {
  display: none;
}
</style>
